import React, { useRef } from 'react';
import classnames from 'classnames';
import { Rating } from '@fiverr-private/data_display';
import { sendMixpanelWidgetClickEvent } from '../../utils/mixpanel';
import { useGigImpression } from '../../hooks/useAfp1Impression';
import { useBQ } from '../../hooks/useBQ';
import { resolveGigImage } from '../../utils/gigImage';
import { priceMarkupFormatter } from '../../utils/priceFormatter';
import { TrackingLink } from '../TrackingLink';
import { Gig } from '../../types';
import { MODE } from '../../../../constants';
import styles from './style.module.scss';

export interface GigCardNewProps {
  className?: string;
  useLargeImage?: boolean;
  horizontal?: boolean;
  gig: Gig;
  affiliateId: string;
  brand: string;
  afp: string;
  afp1: string;
  utmCampaign: string;
  widgetType: MODE;
  onMount?: () => void;
}

interface MarketingParams {
  affiliateId: string;
  utmCampaign: string;
  widgetType: MODE;
  brand: string;
  slug: string;
  afp: string;
  afp1: string;
  afp2: string;
  afp3: string;
}

export const GigCardNew: React.FC<GigCardNewProps> = (props) => {
  const { className, useLargeImage, horizontal, gig, affiliateId, brand, afp, afp1, utmCampaign, widgetType } = props;

  const ref = useRef<HTMLDivElement>(null);
  const { sendBQEvent } = useBQ();

  const { id, url, title, rating, ratingsCount, thumbnail, price, seller } = gig;

  const gigName = url.split('/').pop() || '';
  const gigNameShortened = gigName.length > 50 ? gigName.slice(gigName.length - 50) : gigName;
  const gigNameWithoutAction = gigNameShortened.slice(gigNameShortened.indexOf('-') + 1, gigNameShortened.length);

  const sizeAdjustedThumbnail = resolveGigImage(thumbnail, useLargeImage);

  const marketingParams = (afp3: string): MarketingParams => ({
    affiliateId,
    utmCampaign,
    widgetType,
    brand,
    slug: url,
    afp,
    afp1,
    afp2: gigNameWithoutAction,
    afp3,
  });

  const onGigClick = (): void => {
    sendBQEvent({
      type: 'nga.clicked_gig',
      gigId: id,
      affiliateId,
      widgetType,
      afp1,
    });
  };

  const onSectionClick = (clickSection: string) => (): void => {
    sendMixpanelWidgetClickEvent({
      affiliateId,
      clickName: gigNameWithoutAction,
      clickSection,
      hostReference: afp1,
    });
  };

  useGigImpression({ affiliateId, afp1, gigId: gig.id, widgetType }, ref);

  const displayRatingsCount = ratingsCount || (gig as any).ratingCount || 0;

  return (
    <div
      className={classnames(
        styles.gig,
        {
          [styles.horizontal]: horizontal,
        },
        className
      )}
      ref={ref}
      onClick={onGigClick}
    >
      <TrackingLink className={styles.head} onClick={onSectionClick('gig_image')} {...marketingParams('gig_image')}>
        <img className={styles.image} src={sizeAdjustedThumbnail} alt="gig picture" />
      </TrackingLink>
      <TrackingLink className={styles.seller} onClick={onSectionClick('gig_seller')} {...marketingParams('gig_seller')}>
        <img className={styles['seller-avatar']} src={seller.profilePhoto} alt="seller avatar" />
        {seller.username}
      </TrackingLink>
      <TrackingLink
        className={styles.description}
        onClick={onSectionClick('gig_description')}
        {...marketingParams('gig_description')}
      >
        I will {title}
      </TrackingLink>
      <TrackingLink
        className={styles['rating-link']}
        onClick={onSectionClick('gig_rating')}
        {...marketingParams('gig_rating')}
      >
        <Rating variant="compact" score={rating} reviews={displayRatingsCount.toString()} />
      </TrackingLink>
      <TrackingLink className={styles.price} onClick={onSectionClick('gig_price')} {...marketingParams('gig_price')}>
        <span className={classnames('text-bold', 'co-grey-1200')}>
          From &nbsp;
          <span dangerouslySetInnerHTML={{ __html: priceMarkupFormatter(price) }} />
        </span>
      </TrackingLink>
    </div>
  );
};
